import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  IDepartmentOperationProfileResponse,
  IPutDepartmentOperationProfileRequest,
} from 'types/api/operationsAimApi.interface'
import { IDeptOpsProfilesSlice } from 'types/deptOpsConfig.interface'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import { mapDepartmentProfile } from 'pages/Operations/Shared/opsApiMappers'
import { KPI_TEMPLATE_TO_API } from 'pages/Operations/Shared/opsEnumMaps'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { DEMO_DEPT_PROFILES } from 'pages/Operations/Shared/opsApiDemoCore'

const initialState: IDeptOpsProfilesSlice = {
  isLoading: false,
  profiles: [],
}

export const getDeptOpsProfiles = createAsyncThunk(
  'deptOpsProfiles/getDeptOpsProfiles',
  async (_, thunkAPI) => {
    if (USE_OPS_MOCKS) return DEMO_DEPT_PROFILES.map(mapDepartmentProfile)
    try {
      const resp: AxiosResponse<IDepartmentOperationProfileResponse[]> = await baseUrl.get(
        'aim-operations/department-profile',
      )
      return (resp.data || []).map(mapDepartmentProfile)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateDeptOpsProfile = createAsyncThunk(
  'deptOpsProfiles/updateDeptOpsProfile',
  async (
    { hrDepartmentId, profile }: { hrDepartmentId: number; profile: IOpsDepartment },
    thunkAPI,
  ) => {
    try {
      const body: IPutDepartmentOperationProfileRequest = {
        kpiTemplate: KPI_TEMPLATE_TO_API[profile.kpiTemplate],
        icon: profile.icon,
        sortOrder: profile.sortOrder,
      }
      const resp: AxiosResponse<IDepartmentOperationProfileResponse> = await baseUrl.put(
        `aim-operations/department-profile/${hrDepartmentId}`,
        body,
      )
      toast.success('Department profile saved')
      await thunkAPI.dispatch(getDeptOpsProfiles())
      return mapDepartmentProfile(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const deptOpsProfilesSlice = createSlice({
  name: 'deptOpsProfiles',
  initialState,
  reducers: {
    setKpiTemplate: (
      state,
      action: { payload: { key: string; kpiTemplate: IOpsDepartment['kpiTemplate'] } },
    ) => {
      state.profiles = state.profiles.map((p) =>
        p.key === action.payload.key ? { ...p, kpiTemplate: action.payload.kpiTemplate } : p,
      )
    },
    setSortOrder: (state, action: { payload: { key: string; sortOrder: number } }) => {
      state.profiles = state.profiles.map((p) =>
        p.key === action.payload.key ? { ...p, sortOrder: action.payload.sortOrder } : p,
      )
    },
    clearDeptOpsProfiles: (state) => {
      state.profiles = []
    },
  },
  extraReducers: {
    [getDeptOpsProfiles.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDeptOpsProfiles.fulfilled.type]: (state, { payload }: { payload: IOpsDepartment[] }) => {
      state.isLoading = false
      state.profiles = payload
    },
    [getDeptOpsProfiles.rejected.type]: (state, action) => {
      state.isLoading = false
      handleErrors(action.payload)
    },
    [updateDeptOpsProfile.rejected.type]: (state, action) => handleErrors(action.payload),
  },
})

export const { setKpiTemplate, setSortOrder, clearDeptOpsProfiles } = deptOpsProfilesSlice.actions
export default deptOpsProfilesSlice.reducer
